import React, { useEffect, useState, useCallback } from 'react';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import { isMeasurementsVisible } from 'ducks/layout';
import MeasurementsLabel, { getLabel } from 'components/MeasurementsLabel/MeasurementsLabel';
import {
  LabelPosition,
  MeasurementsEnum,
  MeasurementsWidgetSystem
} from '@inter-ikea-storageone/range';
import { useTranslations } from 'hooks/useTranslations';
import { useServiceSettings } from 'contexts/serviceSettings';

const MeasurementsWidget = () => {
  const measurementsVisible = useSelector(isMeasurementsVisible);
  const [labels, setLabels] = useState<LabelPosition[]>([]);
  const coreApi = useCore();
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();

  const labelsTemporarilyHidden = useCallback(() => {
    if (!coreApi.scene) return false;
    return (
      coreApi.scene?.onlyShowMeasurementIds?.length > 0 &&
      !coreApi.scene?.onlyShowMeasurementIds?.includes(MeasurementsEnum.FrameMeasurements)
    );
  }, [coreApi.scene]);

  const updateLabels = useCallback(() => {
    if (!measurementsVisible) {
      return;
    }

    if (!coreApi.scene) return;
    const outerMeasurements = coreApi.entities.getOuterMeasurementsPerWall();
    const newLabelPositions = coreApi.scene?.getMeasurementsLineCenters();
    if (outerMeasurements && newLabelPositions?.length > 0 && !labelsTemporarilyHidden()) {
      const newLabels = newLabelPositions.flatMap(label => {
        const { dim, wall } = label;
        const measures = outerMeasurements[wall];
        if (measures) {
          const value = measures[dim];
          return getLabel(`${wall}-${dim}`, value, label, translations, useMetric);
        }
        return [];
      });

      setLabels(newLabels);
    } else {
      setLabels([]);
    }
  }, [
    coreApi.entities,
    coreApi.scene,
    measurementsVisible,
    useMetric,
    labelsTemporarilyHidden,
    translations
  ]);

  useEffect(() => {
    const fixedUpdate = () => {
      updateLabels();
    };

    const unsubscribers = [
      coreApi.subscribe('update', fixedUpdate),
      coreApi.subscribe('history', fixedUpdate),
      coreApi.subscribe('modelLoaded', fixedUpdate)
    ];
    return () => {
      unsubscribers.forEach(unsubscribe => {
        unsubscribe();
      });
    };
  }, [coreApi, measurementsVisible, updateLabels, useMetric]);

  useEffect(() => {
    const measurementsWidgetSystem = coreApi.getSystem<MeasurementsWidgetSystem>(
      'MeasurementsWidgetSystem'
    );
    if (measurementsVisible) {
      measurementsWidgetSystem?.enable();
      updateLabels();
    } else {
      measurementsWidgetSystem?.disable();
      setLabels([]);
    }
  }, [coreApi, measurementsVisible, updateLabels]);

  return (
    <>
      {labels.map(({ dim, x, y, size }) => (
        <MeasurementsLabel
          key={dim}
          dim={dim}
          x={x}
          y={y}
          size={size}
          visible={measurementsVisible}
        />
      ))}
    </>
  );
};

export default MeasurementsWidget;
